var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _FacetsFormComponent_instances, preservedParameters_fn, getSearchQuery_fn, updateURLHash_fn, updateSection_fn, _FacetInputsComponent_instances, updateSelectedFacetSummary_fn, _PriceFacetComponent_instances, extractMoneyPlaceholder_fn, _onKeyDown, parseDisplayValue_fn, adjustToValidValues_fn, setMinAndMaxValues_fn, updateSummary_fn, _handleKeyUp, _handleFilterUpdate, _FacetRemoveComponent_instances, withCurrentSort_fn, _handleFilterUpdate2, _SortingFilterComponent_instances, moveFocus_fn, selectOption_fn, closeDropdown_fn, _FacetStatusComponent_instances, updateSwatchSummary_fn, updateBubbleSummary_fn, parseCents_fn, formatMoney_fn;
import { Component } from "@theme/component";
import { FilterUpdateEvent, ThemeEvents } from "@theme/events";
import { convertMoneyToMinorUnits, formatMoney } from "@theme/money-formatting";
import { sectionRenderer } from "@theme/section-renderer";
import { debounce, startViewTransition } from "@theme/utilities";
const SEARCH_QUERY = "q";
const SORT_QUERY = "sort_by";
class FacetsFormComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FacetsFormComponent_instances);
    __publicField(this, "requiredRefs", ["facetsForm"]);
    /**
     * Updates filters and renders the section
     */
    __publicField(this, "updateFilters", () => {
      __privateMethod(this, _FacetsFormComponent_instances, updateURLHash_fn).call(this);
      this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
      __privateMethod(this, _FacetsFormComponent_instances, updateSection_fn).call(this);
    });
  }
  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = __privateMethod(this, _FacetsFormComponent_instances, preservedParameters_fn).call(this);
    for (const [param, value] of new URLSearchParams(
      /** @type any */
      formData
    ).entries()) {
      newParameters.append(param, value);
    }
    if (newParameters.get("filter.v.price.gte") === "") newParameters.delete("filter.v.price.gte");
    if (newParameters.get("filter.v.price.lte") === "") newParameters.delete("filter.v.price.lte");
    newParameters.delete("page");
    const searchQuery = __privateMethod(this, _FacetsFormComponent_instances, getSearchQuery_fn).call(this);
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);
    return newParameters;
  }
  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id) throw new Error("Section ID is required");
    return id;
  }
  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState("", "", url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    __privateMethod(this, _FacetsFormComponent_instances, updateSection_fn).call(this);
  }
}
_FacetsFormComponent_instances = new WeakSet();
/**
 * Current URL parameters that this form isn't responsible for, so they
 * survive a submission from it.
 *
 * Filtering and sorting live in separate drawers on mobile, each with its
 * own facets-form-component and form (blocks/filters.liquid) — the sort
 * drawer's form holds only the sort controls, the filter drawer's only the
 * filters. Since the query string used to be rebuilt from whichever form
 * was submitted, sorting dropped every active filter.* param and filtering
 * dropped the sort. The desktop layout never showed it because there both
 * sets of controls sit in one form.
 *
 * Ownership is read from the form's inputs rather than from its FormData:
 * an unchecked checkbox contributes no entry, and treating that as "not
 * mine" would make clearing the last filter impossible.
 * @returns {URLSearchParams} Parameters carried over from the current URL
 */
preservedParameters_fn = function() {
  const preserved = new URLSearchParams();
  const form = this.refs.facetsForm;
  if (!form) return preserved;
  const ownedNames = new Set(
    Array.from(form.querySelectorAll("[name]")).map((element) => element.getAttribute("name")).filter((name) => name)
  );
  for (const [param, value] of new URLSearchParams(window.location.search).entries()) {
    if (ownedNames.has(param)) continue;
    preserved.append(param, value);
  }
  return preserved;
};
/**
 * Gets the search query parameter from the current URL
 * @returns {string} The search query
 */
getSearchQuery_fn = function() {
  const url = new URL(window.location.href);
  return url.searchParams.get(SEARCH_QUERY) ?? "";
};
/**
 * Updates the URL hash with current filter parameters
 */
updateURLHash_fn = function() {
  const url = new URL(window.location.href);
  const urlParameters = this.createURLParameters();
  url.search = "";
  for (const [param, value] of urlParameters.entries()) {
    url.searchParams.append(param, value);
  }
  history.pushState({ urlParameters: urlParameters.toString() }, "", url.toString());
};
/**
 * Updates the section
 */
updateSection_fn = function() {
  const viewTransition = !this.closest("dialog");
  if (viewTransition) {
    startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ["product-grid"]);
  } else {
    sectionRenderer.renderSection(this.sectionId);
  }
};
if (!customElements.get("facets-form-component")) {
  customElements.define("facets-form-component", FacetsFormComponent);
}
class FacetInputsComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FacetInputsComponent_instances);
    /**
     * Handles mouseover events on facet labels
     * @param {MouseEvent} event - The mouseover event
     */
    __publicField(this, "prefetchPage", debounce((event) => {
      if (!(event.target instanceof HTMLElement)) return;
      const form = this.closest("form");
      if (!form) return;
      const formData = new FormData(form);
      const inputElement = event.target.querySelector("input");
      if (!(inputElement instanceof HTMLInputElement)) return;
      if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);
      const facetsForm = this.closest("facets-form-component");
      if (!(facetsForm instanceof FacetsFormComponent)) return;
      const urlParameters = facetsForm.createURLParameters(formData);
      const url = new URL(window.location.pathname, window.location.origin);
      for (const [key, value] of urlParameters) url.searchParams.append(key, value);
      if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);
      sectionRenderer.getSectionHTML(this.sectionId, true, url);
    }, 200));
    __publicField(this, "cancelPrefetchPage", () => this.prefetchPage.cancel());
  }
  get sectionId() {
    const id = this.closest(".shopify-section")?.id;
    if (!id) throw new Error("FacetInputs component must be a child of a section");
    return id;
  }
  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFilters();
    __privateMethod(this, _FacetInputsComponent_instances, updateSelectedFacetSummary_fn).call(this);
  }
  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector("input");
    if (!(closestInput instanceof HTMLInputElement)) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }
}
_FacetInputsComponent_instances = new WeakSet();
/**
 * Updates the selected facet summary
 */
updateSelectedFacetSummary_fn = function() {
  if (!this.refs.facetInputs) return;
  const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
  const details = this.closest("details");
  const statusComponent = details?.querySelector("facet-status-component");
  if (!(statusComponent instanceof FacetStatusComponent)) return;
  statusComponent.updateListSummary(checkedInputElements);
};
if (!customElements.get("facet-inputs-component")) {
  customElements.define("facet-inputs-component", FacetInputsComponent);
}
class PriceFacetComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _PriceFacetComponent_instances);
    /** @type {string} */
    __publicField(this, "currency");
    /** @type {string} */
    __publicField(this, "moneyFormat");
    /**
     * Handles keydown events to restrict input to valid characters
     * @param {KeyboardEvent} event - The keydown event
     */
    __privateAdd(this, _onKeyDown, (event) => {
      if (event.metaKey) return;
      const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
      if (!event.key.match(pattern)) event.preventDefault();
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", __privateGet(this, _onKeyDown));
    this.currency = this.dataset.currency ?? "USD";
    this.moneyFormat = __privateMethod(this, _PriceFacetComponent_instances, extractMoneyPlaceholder_fn).call(this, this.dataset.moneyFormat ?? "{{amount}}");
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", __privateGet(this, _onKeyDown));
  }
  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    __privateMethod(this, _PriceFacetComponent_instances, adjustToValidValues_fn).call(this, minInput);
    __privateMethod(this, _PriceFacetComponent_instances, adjustToValidValues_fn).call(this, maxInput);
    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFilters();
    __privateMethod(this, _PriceFacetComponent_instances, setMinAndMaxValues_fn).call(this);
    __privateMethod(this, _PriceFacetComponent_instances, updateSummary_fn).call(this);
  }
}
_PriceFacetComponent_instances = new WeakSet();
/**
 * Extracts the placeholder from a money format string, removing currency symbols.
 * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
 * @returns {string} Just the placeholder (e.g., "{{amount}}")
 */
extractMoneyPlaceholder_fn = function(format) {
  const match = format.match(/{{\s*\w+\s*}}/);
  return match ? match[0] : "{{amount}}";
};
_onKeyDown = new WeakMap();
/**
 * Parses a formatted money value into minor units
 * displayValue can come from user input or API response
 * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
 * @param {string} currency - The currency code
 * @returns {number} The value in minor units
 */
parseDisplayValue_fn = function(displayValue, currency) {
  return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
};
/**
 * Adjusts input values to be within valid range
 * @param {HTMLInputElement} input - The input element to adjust
 */
adjustToValidValues_fn = function(input) {
  if (input.value.trim() === "") return;
  const { currency, moneyFormat } = this;
  const value = __privateMethod(this, _PriceFacetComponent_instances, parseDisplayValue_fn).call(this, input.value, currency);
  const min = __privateMethod(this, _PriceFacetComponent_instances, parseDisplayValue_fn).call(this, input.getAttribute("data-min") ?? "0", currency);
  const max = __privateMethod(this, _PriceFacetComponent_instances, parseDisplayValue_fn).call(this, input.getAttribute("data-max") ?? "0", currency);
  if (value < min) {
    input.value = formatMoney(min, moneyFormat, currency);
  } else if (value > max) {
    input.value = formatMoney(max, moneyFormat, currency);
  }
};
/**
 * Sets min and max values for the inputs
 */
setMinAndMaxValues_fn = function() {
  const { minInput, maxInput } = this.refs;
  if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
  if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
  if (minInput.value === "") maxInput.setAttribute("data-min", "0");
  if (maxInput.value === "") minInput.setAttribute("data-max", maxInput.getAttribute("data-max") ?? "");
};
/**
 * Updates the price summary
 */
updateSummary_fn = function() {
  const { minInput, maxInput } = this.refs;
  const details = this.closest("details");
  const statusComponent = details?.querySelector("facet-status-component");
  if (!(statusComponent instanceof FacetStatusComponent)) return;
  statusComponent?.updatePriceSummary(minInput, maxInput);
};
if (!customElements.get("price-facet-component")) {
  customElements.define("price-facet-component", PriceFacetComponent);
}
class FacetClearComponent extends Component {
  constructor() {
    super(...arguments);
    __publicField(this, "requiredRefs", ["clearButton"]);
    /**
     * Handles keyup events
     * @param {KeyboardEvent} event - The keyup event
     */
    __privateAdd(this, _handleKeyUp, (event) => {
      if (event.metaKey) return;
      if (event.key === "Enter") this.clearFilter(event);
    });
    /**
     * Toggle clear button visibility when filters are applied. Happens before the
     * Section Rendering Request resolves.
     *
     * @param {FilterUpdateEvent} event
     */
    __privateAdd(this, _handleFilterUpdate, (event) => {
      const { clearButton } = this.refs;
      if (clearButton instanceof Element) {
        clearButton.classList.toggle("facets__clear--active", event.shouldShowClearAll());
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keyup", __privateGet(this, _handleKeyUp));
    document.addEventListener(ThemeEvents.FilterUpdate, __privateGet(this, _handleFilterUpdate));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, __privateGet(this, _handleFilterUpdate));
  }
  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }
    const container = event.target.closest("facet-inputs-component, price-facet-component");
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = "";
      }
    });
    const details = event.target.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");
    if (!(statusComponent instanceof FacetStatusComponent)) return;
    statusComponent.clearSummary();
    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFilters();
  }
}
_handleKeyUp = new WeakMap();
_handleFilterUpdate = new WeakMap();
if (!customElements.get("facet-clear-component")) {
  customElements.define("facet-clear-component", FacetClearComponent);
}
class FacetRemoveComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FacetRemoveComponent_instances);
    /**
     * Toggle clear button visibility when filters are applied. Happens before the
     * Section Rendering Request resolves.
     *
     * @param {FilterUpdateEvent} event
     */
    __privateAdd(this, _handleFilterUpdate2, (event) => {
      const { clearButton } = this.refs;
      if (clearButton instanceof Element) {
        const activeClass = this.getAttribute("active-class") || "active";
        clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, __privateGet(this, _handleFilterUpdate2));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, __privateGet(this, _handleFilterUpdate2));
  }
  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }
    const url = this.dataset.url;
    if (!url) return;
    const facetsForm = form ? document.getElementById(form) : this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFiltersByURL(__privateMethod(this, _FacetRemoveComponent_instances, withCurrentSort_fn).call(this, url));
  }
}
_FacetRemoveComponent_instances = new WeakSet();
/**
 * Carries the active sort over to a removal URL.
 *
 * Shopify builds `url_to_remove` for individual filter pills with the rest
 * of the query string intact, so those already keep the sort. "Clear all"
 * points at the bare results URL (`collection.url`) instead, which on a
 * collection page has no query string at all — so clearing the filters was
 * also silently resetting the sort back to the collection default.
 *
 * @param {string} url - The removal URL from `data-url`
 * @returns {string} The same URL with the current `sort_by` re-applied
 */
withCurrentSort_fn = function(url) {
  const target = new URL(url, window.location.href);
  const currentSort = new URLSearchParams(window.location.search).get(SORT_QUERY);
  if (currentSort && !target.searchParams.has(SORT_QUERY)) {
    target.searchParams.set(SORT_QUERY, currentSort);
  }
  return target.toString();
};
_handleFilterUpdate2 = new WeakMap();
if (!customElements.get("facet-remove-component")) {
  customElements.define("facet-remove-component", FacetRemoveComponent);
}
class SortingFilterComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _SortingFilterComponent_instances);
    __publicField(this, "requiredRefs", ["details", "summary", "listbox"]);
    /**
     * Handles keyboard navigation in the sorting dropdown
     * @param {KeyboardEvent} event - The keyboard event
     */
    __publicField(this, "handleKeyDown", (event) => {
      const { listbox } = this.refs;
      if (!(listbox instanceof Element)) return;
      const options = Array.from(listbox.querySelectorAll('[role="option"]'));
      const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
      let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
          __privateMethod(this, _SortingFilterComponent_instances, moveFocus_fn).call(this, options, newFocusIndex);
          break;
        case "ArrowUp":
          event.preventDefault();
          newFocusIndex = Math.max(newFocusIndex - 1, 0);
          __privateMethod(this, _SortingFilterComponent_instances, moveFocus_fn).call(this, options, newFocusIndex);
          break;
        case "Enter":
        case " ":
          if (event.target instanceof Element) {
            const targetOption = event.target.closest('[role="option"]');
            if (targetOption) {
              event.preventDefault();
              __privateMethod(this, _SortingFilterComponent_instances, selectOption_fn).call(this, targetOption);
            }
          }
          break;
        case "Escape":
          event.preventDefault();
          __privateMethod(this, _SortingFilterComponent_instances, closeDropdown_fn).call(this);
          break;
      }
    });
    /**
     * Handles details toggle event
     */
    __publicField(this, "handleToggle", () => {
      const { details, summary, listbox } = this.refs;
      if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;
      const isOpen = details.open;
      summary.setAttribute("aria-expanded", isOpen.toString());
      if (isOpen && listbox instanceof Element) {
        const selectedOption = listbox.querySelector('[aria-selected="true"]');
        if (selectedOption instanceof HTMLElement) {
          selectedOption.focus();
        }
      }
    });
  }
  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm = this.closest("facets-form-component") || this.closest(".shopify-section")?.querySelector("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;
    const shouldDisable = this.dataset.shouldUseSelectOnMobile === "true";
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }
    facetsForm.updateFilters();
    this.updateFacetStatus(event);
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }
  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const details = this.querySelector("details");
    if (!details) return;
    const defaultSortBy = details.dataset.defaultSortBy;
    if (event.target instanceof HTMLSelectElement) {
      const facetStatus = details.querySelector("facet-status-component");
      if (!(facetStatus instanceof FacetStatusComponent)) return;
      facetStatus.textContent = event.target.value !== defaultSortBy ? event.target.dataset.optionName ?? "" : "";
    } else if (event.target instanceof HTMLInputElement && event.target.type === "radio" && event.target.name === "sort_by") {
      const statusEl = this.querySelector(".facets__status");
      if (statusEl) {
        statusEl.textContent = event.target.value !== defaultSortBy ? event.target.dataset.optionName ?? "" : "";
      }
    }
  }
}
_SortingFilterComponent_instances = new WeakSet();
/**
 * Moves focus between options
 * @param {Element[]} options - The option elements
 * @param {number} newIndex - The index of the option to focus
 */
moveFocus_fn = function(options, newIndex) {
  options.forEach((option) => {
    if (option instanceof HTMLElement) {
      option.tabIndex = -1;
    }
  });
  const targetOption = options[newIndex];
  if (targetOption instanceof HTMLElement) {
    targetOption.tabIndex = 0;
    targetOption.focus();
  }
};
/**
 * Selects an option and triggers form submission
 * @param {Element} option - The option element to select
 */
selectOption_fn = function(option) {
  const input = option.querySelector('input[type="radio"]');
  if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
    this.querySelectorAll('[role="option"]').forEach((opt) => {
      opt.setAttribute("aria-selected", "false");
    });
    option.setAttribute("aria-selected", "true");
    input.click();
    __privateMethod(this, _SortingFilterComponent_instances, closeDropdown_fn).call(this);
  }
};
/**
 * Closes the dropdown and returns focus to summary
 */
closeDropdown_fn = function() {
  const { details, summary } = this.refs;
  if (details instanceof HTMLDetailsElement) {
    const options = this.querySelectorAll('[role="option"]');
    const selectedOption = this.querySelector('[aria-selected="true"]');
    options.forEach((opt) => {
      if (opt instanceof HTMLElement) {
        opt.tabIndex = -1;
      }
    });
    if (selectedOption instanceof HTMLElement) {
      selectedOption.tabIndex = 0;
    }
    details.open = false;
    if (summary instanceof HTMLElement) {
      summary.focus();
    }
  }
};
if (!customElements.get("sorting-filter-component")) {
  customElements.define("sorting-filter-component", SortingFilterComponent);
}
class FacetStatusComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FacetStatusComponent_instances);
  }
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;
    this.getAttribute("facet-type") === "swatches" ? __privateMethod(this, _FacetStatusComponent_instances, updateSwatchSummary_fn).call(this, checkedInputElements, checkedInputElementsCount) : __privateMethod(this, _FacetStatusComponent_instances, updateBubbleSummary_fn).call(this, checkedInputElements, checkedInputElementsCount);
  }
  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;
    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = "";
      return;
    }
    const currency = facetStatus.dataset.currency || "";
    const minInputNum = __privateMethod(this, _FacetStatusComponent_instances, parseCents_fn).call(this, minInputValue, "0", currency);
    const maxInputNum = __privateMethod(this, _FacetStatusComponent_instances, parseCents_fn).call(this, maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${__privateMethod(this, _FacetStatusComponent_instances, formatMoney_fn).call(this, minInputNum)}\u2013${__privateMethod(this, _FacetStatusComponent_instances, formatMoney_fn).call(this, maxInputNum)}`;
  }
  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = "";
  }
}
_FacetStatusComponent_instances = new WeakSet();
/**
 * Updates the swatch summary
 * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
 * @param {number} checkedInputElementsCount - The number of checked inputs
 */
updateSwatchSummary_fn = function(checkedInputElements, checkedInputElementsCount) {
  const { facetStatus } = this.refs;
  facetStatus.classList.remove("bubble", "facets__bubble");
  if (checkedInputElementsCount === 0) {
    facetStatus.innerHTML = "";
    return;
  }
  if (checkedInputElementsCount > 3) {
    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add("bubble", "facets__bubble");
    return;
  }
  facetStatus.innerHTML = Array.from(checkedInputElements).map((inputElement) => {
    const swatch = inputElement.parentElement?.querySelector("span.swatch");
    const span = document.createElement("span");
    span.className = "visually-hidden";
    span.textContent = inputElement.getAttribute("aria-label") ?? "";
    return (swatch?.outerHTML ?? "") + span.outerHTML;
  }).join("");
};
/**
 * Updates the bubble summary
 * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
 * @param {number} checkedInputElementsCount - The number of checked inputs
 */
updateBubbleSummary_fn = function(checkedInputElements, checkedInputElementsCount) {
  const { facetStatus } = this.refs;
  const filterStyle = this.dataset.filterStyle;
  facetStatus.classList.remove("bubble", "facets__bubble");
  if (checkedInputElementsCount === 0) {
    facetStatus.innerHTML = "";
    return;
  }
  if (filterStyle === "horizontal" && checkedInputElementsCount === 1) {
    facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? "";
    return;
  }
  facetStatus.innerHTML = checkedInputElementsCount.toString();
  facetStatus.classList.add("bubble", "facets__bubble");
};
/**
 * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
 * @param {string} value - The stringified decimal number to parse
 * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
 * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
 * @returns {number} The money value in minor units
 */
parseCents_fn = function(value, fallback = "0", currency = "") {
  const result = convertMoneyToMinorUnits(value, currency);
  if (result !== null) return result;
  const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
  if (fallbackResult !== null) return fallbackResult;
  const cleanFallback = fallback.replace(/[^\d]/g, "");
  return parseInt(cleanFallback, 10) || 0;
};
/**
 * Formats money, replicated the implementation of the `money` liquid filters
 * @param {number} moneyValue - The money value
 * @returns {string} The formatted money value
 */
formatMoney_fn = function(moneyValue) {
  if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return "";
  const format = this.refs.moneyFormat.content.textContent || "{{amount}}";
  const currency = this.refs.facetStatus.dataset.currency || "";
  return formatMoney(moneyValue, format, currency);
};
if (!customElements.get("facet-status-component")) {
  customElements.define("facet-status-component", FacetStatusComponent);
}
